import postClientError from './postClientError';
import { logger } from './telemetry';
import { normalizeError } from './telemetry/scrub';

/**
 * Reports a client error. Unchanged signature: `sendError(error)` at 267 call sites.
 *
 * Now a thin wrapper over the telemetry logger, so every one of those call sites reports to
 * Application Insights with structured dimensions, while the existing POST /Error email channel
 * keeps working. The optional second argument lets a call site add context as it is touched:
 *
 *   sendError(error, { Component: 'CompanyDetailsForm', Operation: 'SaveCompany' });
 *
 * New code should prefer `logger` from lib/telemetry directly, with a message template.
 */

export const invalidEmailMessage = 'Email verified as not safe for sending.';
export const duplicatePerson =
  'This person is already associated to this company';

const abortMessage = 'Abort';

// Suppressed from the email channel only -- these are expected conditions that the UI already
// handles, not faults worth waking anyone for. The list is unchanged in intent from the original.
const errorsNotToEmail = [
  abortMessage,
  'An error occured',
  invalidEmailMessage,
  duplicatePerson,
];

// Suppressed from telemetry as well. Only an aborted request qualifies: it means the user
// navigated away or cancelled, so it is pure noise. Everything else reaches Application
// Insights even when it is not worth an email -- an InvalidEmailException is a real signal.
const errorsNotToLog = [abortMessage];

/**
 * The original compared the message for exact equality, which meant two of the four entries
 * never matched: requests.js wraps the API's bare JSON string, so an invalid-email message
 * arrives as `"\"Email verified as not safe for sending.\""` rather than the bare text.
 * Comparing unquoted, case-insensitive substrings makes the list do what it always intended.
 */
const normalizeForMatch = (text) =>
  typeof text === 'string'
    ? text
        .trim()
        .replace(/^"+|"+$/g, '')
        .trim()
        .toLowerCase()
    : '';

function matchesAny(list, text) {
  const candidate = normalizeForMatch(text);
  if (candidate === '') return false;
  return list.some((entry) => candidate.includes(normalizeForMatch(entry)));
}

// sendError -> UnityApi -> its own catch -> sendError was an unbounded loop. postClientError no
// longer goes through UnityApi, which removes the cycle; this is the second line of defence.
let reporting = false;

export default function sendError(error, context) {
  if (reporting) return;
  reporting = true;

  try {
    const {
      error: reportable,
      template,
      properties,
      matchText,
    } = normalizeError(error);

    const dimensions = {
      ErrorType: 'JavaScript',
      ...properties,
      ...context,
    };

    if (matchesAny(errorsNotToLog, matchText)) {
      // Still visible while developing, never ingested.
      logger.debug(template, dimensions);
    } else {
      logger.error(template, dimensions, reportable);
    }

    if (!matchesAny(errorsNotToEmail, matchText)) {
      postClientError({
        type: dimensions.ErrorType,
        // The scrubbed copy: this text is also what lands in Application Insights, and the
        // API's response body can carry names, emails and phone numbers.
        errorMessage: reportable.message,
        stackTrace: reportable.stack ?? '',
        url: window.location.href,
      });
    }
  } catch {
    // A failure inside the error reporter must not surface as a new error.
  } finally {
    reporting = false;
  }
}
